import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, invoiceStatus, notifyCompanyUsers, clientPayable, applyApprovedPayment, resolveInvoiceFinancials, instalmentFinancials } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { sendInvoiceEmail } from '../../api';
import { InvoicePrintView } from './InvoicePrintView';
import { INVOICE_PRINT_CSS, INVOICE_PRINT_MOBILE_CSS } from './invoicePrintStyles';
import { isNativeApp } from '../../core/platform';
function PaymentSchedule({ invoice }) {
  const { showToast, can, user, openModal, paymentSchedule } = useApp();
  const [adding, setAdding] = useState(false);
  const [form, setForm]     = useState({ label:"", amount:"", dueDate:"" });

  const rows = useMemo(
    () => paymentSchedule.filter(s => s.invoiceId === invoice.id).sort((a, b) => (a.seq || 0) - (b.seq || 0)),
    [paymentSchedule, invoice.id],
  );

  const rowStatus = (r) => {
    const fin = instalmentFinancials(r);
    if (fin.status === "partial") return "partial";
    const due = r.dueDate || r.due_date;
    if (r.status === "unpaid" && due && new Date(due) < new Date(new Date().toDateString())) return "overdue";
    return fin.status || r.status || "unpaid";
  };
  const addRow = async () => {
    if (!form.label || !form.amount) { showToast("Label and amount are required", "error"); return; }
    // #35 Scheduled instalments must not exceed the invoice total
    const scheduled = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const remaining = (Number(invoice.total) || 0) - scheduled;
    if (Number(form.amount) > remaining + 0.01) {
      showToast(`Amount exceeds the ₹${remaining.toLocaleString("en-IN")} still unscheduled on this invoice`, "error"); return;
    }
    await sb.insert("payment_schedule", { invoice_id:invoice.id, company_id:invoice.clientId, seq:(rows.length+1), label:form.label, amount:Number(form.amount), due_date:form.dueDate||null, status:"unpaid" });
    setForm({ label:"", amount:"", dueDate:"" }); setAdding(false);
  };
  const mark = async (r, status) => {
    const fin = instalmentFinancials(r);
    const patch = status === "paid"
      ? { status, paid_amount: fin.amount, paid_at: new Date().toISOString() }
      : { status, paid_at: status === "paid" ? new Date().toISOString() : null };
    await sb.update("payment_schedule","id",r.id, patch);
  };

  return (
    <div className="card" style={{ marginTop:16 }}>
      <div className="card-head" style={{ display:"flex", alignItems:"center" }}>
        <div className="card-title">Payment Schedule</div>
        {can("invoices","edit") && <button className="btn btn-sm" style={{ marginLeft:"auto" }} onClick={()=>setAdding(a=>!a)}>+ Add milestone / instalment</button>}
      </div>
      <div className="card-body">
        {adding && (
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", marginBottom:12, flexWrap:"wrap" }}>
            <div className="f-group" style={{ flex:1, minWidth:160 }}><label className="f-label">Label</label><input className="f-input" placeholder="Advance / Balance / Instalment 1 of 3" value={form.label} onChange={e=>setForm(f=>({ ...f, label:e.target.value }))}/></div>
            <div className="f-group" style={{ width:120 }}><label className="f-label">Amount ₹</label><input className="f-input" type="number" value={form.amount} onChange={e=>setForm(f=>({ ...f, amount:e.target.value }))}/></div>
            <div className="f-group" style={{ width:160 }}><label className="f-label">Due date (optional)</label><input className="f-input" type="date" value={form.dueDate} onChange={e=>setForm(f=>({ ...f, dueDate:e.target.value }))}/></div>
            <button className="btn btn-primary btn-sm" onClick={addRow}>Add</button>
          </div>
        )}
        {rows.length===0 && !adding && <div style={{ fontSize:12, color:"var(--muted)", padding:"8px 0" }}>No schedule — this invoice is billed in full. Add milestones or instalments above.</div>}
        {rows.map(r => {
          const fin = instalmentFinancials(r);
          const st = rowStatus(r);
          return (
          <div key={r.id} className="row-item">
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{r.label}</div>
              <div style={{ fontSize:11, color:"var(--muted)" }}>{(r.dueDate || r.due_date) ? `Due ${r.dueDate || r.due_date}` : (r.triggerEvent || r.trigger_event || "On milestone")}</div>
              {fin.paid > 0 && (
                <div style={{ fontSize:11, marginTop:4 }}>
                  <span style={{ color:"var(--green)" }}>Paid {INR(fin.paid)}</span>
                  {fin.residual > 0.01 && <span style={{ color:"var(--red)", marginLeft:8 }}>Due {INR(fin.residual)}</span>}
                </div>
              )}
            </div>
            <div style={{ textAlign:"right", marginRight:12 }}>
              <div style={{ fontWeight:700 }}>{INR(fin.amount)}</div>
              {fin.paid > 0 && fin.residual > 0.01 && (
                <div style={{ fontSize:10, color:"var(--muted)" }}>{INR(fin.residual)} left</div>
              )}
            </div>
            <Badge status={st} />
            {st !== "paid" && fin.residual > 0.01 && (
              <div style={{ display:"flex", gap:6, marginLeft:10 }}>
                {user?.role === "client"
                  ? (r.status==="unpaid" || st==="partial") && <button className="btn btn-sm btn-primary" onClick={()=>openModal("pay-now",{ invoiceId: invoice.id, amount: fin.residual, scheduleId: r.id })}><InlineIcon name="card" size={13} gap={5}>Pay {INR(fin.residual)}</InlineIcon></button>
                  : (can("invoices","edit") && <>
                      {(r.status==="unpaid" || st==="partial") && <button className="btn btn-sm" onClick={()=>mark(r,"pending_review")}>Mark Received</button>}
                      {r.status==="pending_review" && <button className="btn btn-sm" style={{ background:"var(--green)", color:"#fff", border:"none" }} onClick={()=>mark(r,"paid")}><InlineIcon name="check" size={13} gap={5}>Approve</InlineIcon></button>}
                    </>)
                }
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function ViewInvoiceModal({ data, onClose }) {
  const { invoice: rawInvoice } = data;
  const { org: orgLive, openModal, user, clients, users, userLinks, showToast, can, payments, paymentSchedule, setInvoices, setPayments, setClients } = useApp();
  const org = rawInvoice.letterhead || orgLive;
  const invoice = resolveInvoiceFinancials(rawInvoice, payments);

  const isClient = user?.role === "client";
  const payable = clientPayable(invoice, paymentSchedule);

  const client    = clients.find(c => c.id === invoice.clientId);
  const subtotal  = (invoice.lineItems||[]).reduce((s,li)=>s+(li.unitPrice||0)*(li.qty||1),0);
  const gstAmt    = (invoice.total||0) - subtotal;
  // IGST if different state, CGST+SGST if same state (Karnataka default)
  const clientState = client?.state || "";
  const orgState    = org.state || "Karnataka";
  const isSameState = !clientState || clientState.toLowerCase() === orgState.toLowerCase();
  const cgst = isSameState ? gstAmt / 2 : 0;
  const sgst = isSameState ? gstAmt / 2 : 0;
  const igst = isSameState ? 0 : gstAmt;
  const isPaid    = invoice.status === "paid";
  const isPartial = invoice.status === "partial";
  const isVoid    = invoice.status === "void";

  // #G14 Void / cancel an invoice (team only) — writes off the balance with a reason + audit trail
  const [voiding, setVoiding]       = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const doVoid = async () => {
    if (!voidReason.trim()) { showToast("Add a reason for voiding this invoice", "error"); return; }
    try {
      await sb.createDeleteRequest({
        entity_type: "invoices",
        entity_id: invoice.id,
        reason: voidReason.trim(),
        company_id: invoice.clientId,
        reassign_to: null,
      });
      showToast("Delete/void request submitted — awaiting admin approval", "success");
      setVoiding(false);
      setVoidReason("");
      onClose();
    } catch (err) {
      showToast(err?.message || "Failed to submit delete request", "error");
    }
  };

  const approvePayment = async () => {
    const pending = (payments || []).filter(p =>
      p.invoiceId === invoice.id && p.status === "pending_approval"
    );
    if (pending.length) {
      let current = { ...invoice };
      for (const p of pending) {
        const result = await applyApprovedPayment({
          payment: p,
          invoice: current,
          sb,
          setInvoices,
          setPayments,
          setClients,
        });
        current = { ...current, ...result };
      }
    } else {
      const finalStatus = (invoice.pending <= 0) ? "paid" : "partial";
      await sb.update("invoices", "id", invoice.id, {
        status: finalStatus,
        pending: Math.max(0, invoice.pending || 0),
        paid: invoice.paid || 0,
      });
      setInvoices?.(is => is.map(i => (
        i.id === invoice.id ? { ...i, status: finalStatus } : i
      )));
    }
    showToast("Payment approved — marked Received", "success");
    await notifyCompanyUsers(invoice.clientId, { userLinks, users, clients }, {
      type: "payment",
      title: "Payment confirmed",
      body: `Your payment for invoice ${invoice.invoiceNo} has been confirmed.`,
      meta: { invoiceId: invoice.id, modal: "view-invoice" },
    });
    onClose();
  };

  const emailToClient = async () => {
    if (!client?.email) { showToast("Client has no email on file", "error"); return; }
    setSendingEmail(true);
    try {
      await sendInvoiceEmail(invoice.id, client.email);
      showToast(`Invoice emailed to ${client.email}`, "success");
    } catch (e) {
      showToast(e.message || "Could not send email", "error");
    } finally {
      setSendingEmail(false);
    }
  };

  const printInvoice = () => {
    const w = window.open("", "_blank", "width=850,height=1000");
    const content = document.getElementById("invoice-print-area");
    w.document.write(`<html><head><title>Invoice ${invoice.invoiceNo}</title><style>${INVOICE_PRINT_CSS}</style></head><body>${content?.outerHTML || ""}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  return (
    <div className="modal-box invoice-view-modal" style={{ maxWidth:820 }}>
      <div className="modal-head">
        <div>
          <div className="modal-title">Invoice {invoice.invoiceNo}</div>
          <div style={{ fontSize:12,color:"var(--muted)",marginTop:2 }}>
            {invoice.date}{invoice.dueDate && ` · Due ${invoice.dueDate}`}
          </div>
        </div>
        <div style={{ display:"flex",gap:8,alignItems:"center" }}>
          <Badge status={invoiceStatus(invoice)} />
          {(invoice.status === "pending_review" || invoice.status === "pending_approval") && !isClient && (can("invoices", "edit") || can("payments", "edit")) && (
            <button className="btn btn-sm" style={{background:"#F0FDF4",color:"var(--green)",border:"1px solid #86EFAC"}} onClick={approvePayment}><InlineIcon name="check" size={13} gap={5}>Approve Payment</InlineIcon></button>
          )}
          <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
        </div>
      </div>
      <div className="modal-body" style={{ padding: 0 }}>
        <style dangerouslySetInnerHTML={{ __html: INVOICE_PRINT_CSS + (isNativeApp() ? INVOICE_PRINT_MOBILE_CSS : '') }} />
        <div style={{ padding: isNativeApp() ? "6px 4px" : "12px 16px", background: "#fff" }}>
          <InvoicePrintView
            invoice={invoice}
            org={org}
            client={client}
            subtotal={subtotal}
            cgst={cgst}
            sgst={sgst}
            igst={igst}
            isSameState={isSameState}
            payable={payable}
            isClient={isClient}
          />
        </div>
        {isPaid && (
          <div style={{ margin: "0 24px 16px", padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, fontSize: 11, color: "var(--green)", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
            <Icon name="check" size={13} /> This invoice has been fully paid. Thank you!
          </div>
        )}
        {isVoid && (
          <div style={{ margin: "0 24px 16px", padding: "10px 14px", background: "#F3F4F6", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: 11, color: "#4B5563", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
            <Icon name="ban" size={13} /> This invoice has been <strong>voided / cancelled</strong>{invoice.voidReason ? ` — ${invoice.voidReason}` : ""}.
          </div>
        )}
      </div>
      <PaymentSchedule invoice={invoice} />
      {voiding && !isClient && (
        <div style={{ padding:"12px 16px", background:"#FEF2F2", borderTop:"1px solid #FCA5A5" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#991B1B", marginBottom:6 }}>Request delete / void — an admin must approve before this invoice is cancelled.</div>
          <textarea className="f-textarea" rows={2} placeholder="Reason (required) — e.g. raised in error, duplicate, service cancelled" value={voidReason} onChange={e=>setVoidReason(e.target.value)} style={{ marginBottom:8 }} />
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-sm" onClick={()=>{ setVoiding(false); setVoidReason(""); }}>Cancel</button>
            <button className="btn btn-sm btn-red" onClick={doVoid}><InlineIcon name="ban" size={13} gap={5}>Submit for approval</InlineIcon></button>
          </div>
        </div>
      )}
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Close</button>
        {payable.amount > 0 && isClient && (
          <button className="btn btn-green" onClick={()=>{onClose();openModal("pay-now",{invoiceId:invoice.id, amount: payable.amount, scheduleId: payable.scheduleId || undefined});}}>
            <InlineIcon name="card" size={14} gap={5}>Pay {INR(payable.amount)}</InlineIcon>
          </button>
        )}
        {invoice.pending > 0 && !isClient && (
          <button className="btn btn-sm" style={{background:"var(--green)",color:"#fff",border:"none"}}
            onClick={()=>{onClose();openModal("record-payment",{invoiceId:invoice.id});}}>
            <InlineIcon name="money" size={13} gap={5}>Record Payment</InlineIcon>
          </button>
        )}
        {!isClient && !isVoid && !isPaid && can("invoices","edit") && !voiding && (
          <button className="btn btn-sm" style={{ background:"#fff", color:"#B91C1C", border:"1px solid #FCA5A5" }} onClick={()=>setVoiding(true)}><InlineIcon name="ban" size={13} gap={5}>Delete / Void</InlineIcon></button>
        )}
        {!isClient && !isVoid && client?.email && (
          <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "var(--blue)", border: "1px solid #BFDBFE" }}
            onClick={emailToClient} disabled={sendingEmail}>
            <InlineIcon name="mail" size={13} gap={5}>{sendingEmail ? "Sending…" : "Email to Client"}</InlineIcon>
          </button>
        )}
        <button className="btn btn-primary" onClick={printInvoice}><InlineIcon name="doc" size={14} gap={5}>Print / PDF</InlineIcon></button>
      </div>
    </div>
  );
}

export { PaymentSchedule, ViewInvoiceModal };
